import axios, {
    AxiosRequestConfig,
    AxiosResponse,
} from 'axios';
import servicesConfig, { IServiceConfig } from './servicesConfig';
import { getLinkId, getRequestToken, isTimedLinkAvailable, isThirdPartyTokenAuthorization, isPreview } from '../utils/general/general';
import { loaderService } from './loader-services';

export interface IService {
    ajaxOptions: object;
    serviceDetail: IServiceConfig;
    masklayer: string;
    cache: boolean;
    dataType: string;
}

export interface IServiceOption {
    contentType?: string;
    data: object;
    method: string;
    url: string;
}

export interface IServiceFunctions {
    getData: (serviceDetail: IServiceConfig | any, paramData: object, masklayer: string) => Promise<any>;
}

// Add a request interceptor
axios.interceptors.request.use(
    (config) => {
        // called before request is sent   
        loaderService.showSpinner();        
        return config;
    },
    (error) => {
        // handle request error
        console.log("Axios request error:", error)
        return Promise.reject(error);
    }
);

// Add a response interceptor
axios.interceptors.response.use(
    (response) => {
        // Any status code within the range of 2xx cause this function to trigger
        // with response data
        loaderService.hideSpinner();
        return response;
    },
    (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // with response error
        loaderService.hideSpinner();
        return Promise.reject(error);
    }
)

const requestsHolder: { [index: string]: any } = {};

function fireRequest(axiosOptions: IServiceOption, serviceDetail: IServiceConfig, masklayer: string, token: any = {}): Promise<any> {
    const shouldAbortOldRequests: boolean = (serviceDetail && serviceDetail.abort_previous_requests);
    const showmask = (serviceDetail && serviceDetail.showmask);
    try {
        const headers = { 'Content-Type': 'application/json;charset=utf-8', 'Cache-Control': 'no-cache' };
        if (isTimedLinkAvailable === true && getLinkId()){
            Object.assign(headers, {linkId: getLinkId()});
        }
        if (window.HTML_RENDITION.initialData.isPublic){
            Object.assign(headers, {cgiDocId: window.HTML_RENDITION.initialData.doc_id});
            if (isPreview()){
                Object.assign(headers, {cgiPreview: true});
            }
        }
        if (isThirdPartyTokenAuthorization() && token?.access_token) {
            Object.assign(headers, {Authorization: 'bearer ' + token.access_token});
        }
        if (typeof serviceDetail == 'object') {
            // Set last uri token as 'identifier'
            if (serviceDetail.headers) {
                Object.assign(headers, serviceDetail.headers);
            }
            const identifier: string = serviceDetail.url_tokens.slice().pop() || '';
            if (shouldAbortOldRequests) {
                if (requestsHolder[identifier] === undefined) {
                    requestsHolder[identifier] = [];
                }
                while (requestsHolder[identifier].length > 0) {
                    requestsHolder[identifier].shift();
                }
            }

            let html_features_entitled: any = null;
            const getHTMLFeatureEntitledVal = identifier.indexOf('header') > -1 ||
                                              identifier.indexOf('body') > -1 ||
                                              identifier.indexOf('footer') > -1 ||
                                              (window.HTML_RENDITION.isMCN &&  identifier.indexOf('section') > -1);
            if (window.HTML_RENDITION.initialData.html_features_entitled && getHTMLFeatureEntitledVal) {
                html_features_entitled = window.HTML_RENDITION.initialData.html_features_entitled;
            }
            const defaultOptions: AxiosRequestConfig = {
                headers,
                // responseType: 'stream',
            };

            if (html_features_entitled && defaultOptions?.headers) {
                defaultOptions.headers.html_features_enabled = html_features_entitled;
            }

            const axiosConfig: AxiosRequestConfig = Object.assign(defaultOptions, axiosOptions);
            const axiosPromise: Promise<AxiosResponse> = axios(axiosConfig).catch((error: any) => {
                console.error(error); throw error;
            });
            if (shouldAbortOldRequests) {
                requestsHolder[identifier].push(axiosPromise);
            }
            return axiosPromise;
        } else {
            return axios.get(serviceDetail, {headers}).catch((error: any) => {
                console.error(error); throw error;
            });
        }
    } catch (e) {
        console.error(e);
        return Promise.reject(e);
    }
}

export function getData(serviceDetail: IServiceConfig | any, paramData: object, masklayer: string) {
    let options: any = null;
    if (typeof serviceDetail == 'object') {
        options = {
            data: paramData, // The URL's parameters
            method: servicesConfig.GET_METHOD(serviceDetail), // POST or GET, default value is GET
            url: servicesConfig.GET_URL(serviceDetail),
        };
        const contentType = servicesConfig.GET_CONTENT_TYPE(serviceDetail);
        if (contentType) {
            Object.assign(options, contentType);
        }
    }
    if (isThirdPartyTokenAuthorization()) {
        return getRequestToken().then((token: any) => {
            return fireRequest(options, serviceDetail, masklayer, token);
        });
    } else {
        return fireRequest(options, serviceDetail, masklayer);
    }
}

const SERVICES: IServiceFunctions = {
    getData,
};

export default SERVICES;
